import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { commonDateFormat, dayjs } from '../../Utils/DateUtils';
import {
  fetchFavoriteSearch,
  fetchRecentSearch,
  fetchSearchLoads,
  fetchLoadById,
  fetchTypeAhead,
  fetchUserWatchedLoads,
  modifyFavoriteSearch,
  modifyWatchedLoads,
  postBookLoad,
} from '../APIServices/SearchService';
import { activateLoading, deactivateLoading } from './GlobalSlice';
import { dropDateOffset, KEYS, pickupDateOffset } from '../../Constants/Constants';
import { TSearchTabs } from '../../Pages/Search/SearchContainer.type';
import { searchTabs } from '../../Pages/Search/SearchContainer';
import { sortTypeOptions } from '../../Components/Search/NewSearchContainer/NewSearchContainer.type';
interface ISearchQuery {
  originLocation: any;
  originRadius: number;
  originFromDate: string;
  originToDate: string;
  destinationLocation: any;
  destinationRadius: number;
  destinationFromDate: string;
  destinationToDate: string;
  excludeSpecialServices?: any;
  minRPM?: any;
  maxMiles?: any;
  minMiles?: any;
}
// export interface ISearchQueryError {
//   originLocation: string;
//   originFromDate: string;
//   originToDate: string;
//   destinationLocation: string;
//   destinationFromDate: string;
//   destinationToDate: string;
// }
export type TSearchQueryError = Partial<
  Record<
    | 'originFromDate'
    | 'originToDate'
    | 'destinationFromDate'
    | 'destinationToDate'
    | 'originLocation'
    | 'destinationLocation',
    string
  >
>;
export const InitialSearchQuery: ISearchQuery = {
  originLocation: {},
  destinationLocation: {},
  originRadius: 0,
  destinationRadius: 0,
  originFromDate: commonDateFormat(dayjs()) as string,
  originToDate: commonDateFormat(dayjs().add(pickupDateOffset, 'day')) as string,
  destinationFromDate: commonDateFormat(dayjs()) as string,
  destinationToDate: commonDateFormat(dayjs().add(dropDateOffset, 'day')) as string,
};
export type TsortAndFilter = {
  size: number;
  page: number;
  sortColumn: string;
  sortDirection: string;
};
export interface IsearchState {
  searchQuery: ISearchQuery;
  searchQueryError: TSearchQueryError;
  recentSearchData: any[];
  favoriteSearchData: any[];
  watchedLoadsData: any;
  searchSectionSelectedTab: TSearchTabs[];
  selectedTab: string;
  selectedHomeTab: string;
  searchById: boolean;
  selectedTopCardData: any;
  searchResultData: any;
  sortAndFilter: TsortAndFilter;
  bookLoadData: any;
  loadByIdData: any;
  showSearchResult: boolean;
  resetToggleFilter: boolean;
}
const InitialSearchQueryError: TSearchQueryError = {
  originLocation: '',
  destinationLocation: '',
  originFromDate: '',
  originToDate: '',
  destinationFromDate: '',
  destinationToDate: '',
};
const InitialSortAndFilter = {
  size: KEYS?.RESULT_PER_PAGE_MAX_LOAD_CAPACITY,
  page: 0,
  sortColumn: sortTypeOptions[1]?.slug,
  sortDirection: 'asc',
};

export const initialSearchState: IsearchState = {
  searchQuery: { ...InitialSearchQuery },
  searchQueryError: { ...InitialSearchQueryError },
  recentSearchData: [],
  favoriteSearchData: [],
  searchResultData: {
    totalRecords: 0,
    loadPostings: [],
  },
  watchedLoadsData: {
    totalRecords: 0,
    loadPostings: [],
  },
  searchSectionSelectedTab: [...searchTabs],
  selectedTab: '',
  selectedHomeTab: '',
  searchById: false,
  selectedTopCardData: null,
  sortAndFilter: { ...InitialSortAndFilter },
  bookLoadData: {},
  loadByIdData: null,
  showSearchResult: false,
  resetToggleFilter: false,
};

const initialState: IsearchState = { ...initialSearchState };
export const searchSlice = createSlice({
  name: 'Search',
  initialState,
  reducers: {
    handleResetSearchQuery: (state) => {
      state.searchQuery = { ...InitialSearchQuery };
    },
    handleResetSortAndFilter: (state) => {
      state.sortAndFilter = { ...InitialSortAndFilter };
    },
    handleResetPaginationFilter: (state) => {
      state.sortAndFilter = { ...state?.sortAndFilter, page: 0 };
    },
    updateSearchQuery: (state, action: PayloadAction<ISearchQuery>) => {
      state.searchQuery = action?.payload;
    },
    handleResetSearchQueryError: (state) => {
      state.searchQueryError = { ...InitialSearchQueryError };
    },
    updateSearchQueryError: (state, action: PayloadAction<TSearchQueryError>) => {
      state.searchQueryError = action?.payload;
    },
    updateSearchSectionSelectedTab: (state, action: PayloadAction<TSearchTabs[]>) => {
      state.searchSectionSelectedTab = action?.payload;
    },
    updateHomeSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedHomeTab = action?.payload;
    },
    handleSearchById: (state, action: PayloadAction<any>) => {
      state.searchById = action?.payload;
    },
    clearGlobalSearch: (state) => {
      state.loadByIdData = null;
      state.searchById = false;
    },
    updateShowSearchResult(state, action: PayloadAction<any>) {
      state.showSearchResult = action?.payload;
    },
    handleResetToggleFilter(state, action: PayloadAction<any>) {
      state.resetToggleFilter = action?.payload;
    },
    handleSelectedTopCardData: (state, action: PayloadAction<any>) => {
      state.selectedTopCardData = action?.payload;
    },
    updateSortAndFilter: (state, action: PayloadAction<TsortAndFilter>) => {
      state.sortAndFilter = action?.payload;
    },
    updateSearchResultData: (state, action: PayloadAction<any>) => {
      state.searchResultData = action?.payload;
    },
    updateLoadByIdData: (state, action: PayloadAction<any>) => {
      state.loadByIdData = action?.payload;
    },
    updateWatchedLoadsDataLocal: (state, action: PayloadAction<any>) => {
      if (action?.payload?.id) {
        state.watchedLoadsData.totalRecords = state.watchedLoadsData.totalRecords - 1;
        state.watchedLoadsData.loadPostings = state.watchedLoadsData.loadPostings.filter(
          (load: any) => load?.id !== action?.payload?.id,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRecentSearch.fulfilled, (state, action) => {
      if (action?.payload) {
        state.recentSearchData = action?.payload;
      }
    });
    builder.addCase(getFavoriteSearch.fulfilled, (state, action) => {
      if (action?.payload) {
        state.favoriteSearchData = action?.payload;
      }
    });
    builder.addCase(getUserWatchedLoads.fulfilled, (state, action) => {
      if (action?.payload?.response) {
        if (action?.payload?.queryPayloads?.page > 0) {
          state.watchedLoadsData.totalRecords = action?.payload?.response?.totalRecords;
          state.watchedLoadsData.loadPostings = [
            ...state.watchedLoadsData.loadPostings,
            ...action?.payload?.response?.loadPostings,
          ];
        } else {
          state.watchedLoadsData.totalRecords = action?.payload?.response?.totalRecords;
          state.watchedLoadsData.loadPostings = action?.payload?.response?.loadPostings;
        }
      }
    });
    builder.addCase(getSearchLoads.fulfilled, (state, action) => {
      if (action?.payload?.response?.status) {
        if (action?.payload?.queryPayloads?.page > 0) {
          state.searchResultData.totalRecords = action?.payload?.response?.totalRecords;
          state.searchResultData.loadPostings = [
            ...state.searchResultData.loadPostings,
            ...action?.payload?.response?.loadPostings,
          ];
        } else {
          state.searchResultData.totalRecords = action?.payload?.response?.totalRecords;
          state.searchResultData.loadPostings = action?.payload?.response?.loadPostings;
        }
      }
    });
    builder.addCase(bookLoad.fulfilled, (state, action) => {
      if (action?.payload) {
        state.bookLoadData = action.payload?.response?.loadPostings;
      }
    });
    builder.addCase(getLoadById.fulfilled, (state, action) => {
      if (action?.payload?.response?.loadPostings.length) {
        state.loadByIdData = { ...action.payload.response.loadPostings[0] };
      } else {
        state.loadByIdData = {};
      }
    });
  },
});

export const {
  handleResetSearchQuery,
  handleResetSortAndFilter,
  handleResetPaginationFilter,
  updateSearchQuery,
  handleResetSearchQueryError,
  updateSearchQueryError,
  updateSearchSectionSelectedTab,
  updateHomeSelectedTab,
  handleSelectedTopCardData,
  handleSearchById,
  clearGlobalSearch,
  updateShowSearchResult,
  updateSortAndFilter,
  updateSearchResultData,
  handleResetToggleFilter,
  updateLoadByIdData,
  updateWatchedLoadsDataLocal,
} = searchSlice.actions;
export default searchSlice.reducer;

export const typeAheadCall = createAsyncThunk(
  'Search/typeAheadCall',
  async (payLoad: any, { dispatch }) => {
    dispatch(activateLoading());
    const response: any = await fetchTypeAhead(payLoad?.queryPayloads, payLoad?.callback);
    dispatch(deactivateLoading());
    return response;
  },
);
export const getRecentSearch = createAsyncThunk(
  'Search/getRecentSearch',
  async (payLoad: any, { dispatch }) => {
    try {
      dispatch(activateLoading());
      const response: any = await fetchRecentSearch(payLoad);
      return response;
    } catch (error) {
      return null;
    } finally {
      dispatch(deactivateLoading());
    }
  },
);
export const getFavoriteSearch = createAsyncThunk(
  'Search/getFavoriteSearch',
  async (payLoad: any, { dispatch }) => {
    try {
      dispatch(activateLoading());
      const response: any = await fetchFavoriteSearch(payLoad);
      return response;
    } catch (error) {
      return null;
    } finally {
      dispatch(deactivateLoading());
    }
  },
);
export const updateFavoriteSearch = createAsyncThunk(
  'Search/getFavoriteSearch',
  async (payLoad: any, { dispatch }) => {
    try {
      dispatch(activateLoading());
      const response: any = await modifyFavoriteSearch(payLoad);
      return response;
    } catch (error) {
      return null;
    } finally {
      dispatch(deactivateLoading());
    }
  },
);
export const getUserWatchedLoads = createAsyncThunk(
  'Search/getUserWatchedLoads',
  async (payLoad: any, { dispatch }) => {
    try {
      dispatch(activateLoading());
      const response: any = await fetchUserWatchedLoads(payLoad);
      return { queryPayloads: payLoad, response };
    } catch (error) {
      return null;
    } finally {
      dispatch(deactivateLoading());
    }
  },
);
export const updateUserWatchedLoads = createAsyncThunk(
  'Search/updateUserWatchedLoads',
  async (payLoad: any, { dispatch }) => {
    try {
      dispatch(activateLoading());
      const response: any = await modifyWatchedLoads(payLoad);
      return response;
    } catch (error) {
      return null;
    } finally {
      dispatch(deactivateLoading());
    }
  },
);
export const getSearchLoads = createAsyncThunk(
  'Search/getSearchLoads',
  async (payLoad: any, { dispatch }) => {
    try {
      !payLoad?.hideLoader && dispatch(activateLoading());
      const response: any = await fetchSearchLoads(payLoad?.queryPayloads);
      !payLoad?.hideLoader && dispatch(deactivateLoading());
      return { queryPayloads: payLoad?.queryPayloads, response };
    } catch (error) {
      dispatch(deactivateLoading());
      return null;
    } finally {
      payLoad?.callback && payLoad?.callback();
    }
  },
);
export const getLoadById = createAsyncThunk(
  'Search/getLoadById',
  async (payLoad: any, { dispatch }) => {
    try {
      dispatch(activateLoading());
      const response: any = await fetchLoadById(payLoad);
      return { payLoad, response };
    } catch (error) {
      return null;
    } finally {
      dispatch(deactivateLoading());
    }
  },
);
export const bookLoad = createAsyncThunk('load/bookLoad', async (payLoad: any, { dispatch }) => {
  try {
    dispatch(activateLoading());
    const response: any = await postBookLoad(payLoad);
    return response;
  } catch (error) {
    return null;
  } finally {
    dispatch(deactivateLoading());
  }
});
